/**
 * Fuente única de verdad del locale de MomentJS para toda la aplicación.
 *
 * ⚠ Debe importarse antes que cualquier otro módulo que use moment o maz-ui
 *   (ver src/main.js). No mover.
 *
 * MazPicker (maz-ui 2.4.7) define el default de su prop `locale` como
 * `navigator.language.substr(0, 2)` y, con un watcher `immediate: true`,
 * ejecuta `moment.locale(<prop>)` cada vez que se crea un picker. Como moment
 * es un "external" de maz-ui, eso muta el locale GLOBAL de la app.
 *
 * El calendario mezcla ambas fuentes:
 *   nombres de los días  -> moment.weekdaysShort()                    (GLOBAL)
 *   rotación (1er día)   -> moment.localeData(prop).firstDayOfWeek()  (PROP)
 *   huecos de la rejilla -> moment(...).weekday()                     (GLOBAL)
 *
 * Si PROP y GLOBAL difieren en `dow` la rejilla se desfasa una columna
 * ("es" => dow 1, "es-mx"/"es-us" => dow 0). Y como ~100 módulos hacían
 * `moment.locale(...)` en scope de módulo, quién ganaba dependía del orden de
 * evaluación de los chunks: de ahí que el calendario cambiara al recargar con
 * F5 y que cerrar/reabrir el modal de Filtros lo "arreglara".
 *
 * Solución: PROP == GLOBAL == "es-mx", fijado una sola vez antes de crear
 * cualquier instancia de Vue.
 */
import moment from "moment";
// Registro explícito: hoy webpack empaqueta todos los locales de moment, pero
// esto lo hace inmune a que se agregue ContextReplacementPlugin más adelante.
import "moment/locale/es-mx";
import { MazPicker } from "maz-ui";

/**
 * Locale único de la aplicación. Español de México, la semana inicia en domingo.
 *
 * Este es el ÚNICO punto donde se decide el locale. Para que el calendario siga
 * el idioma del navegador basta con cambiar esta constante por un resolvedor,
 * pero eso requiere antes eliminar los ~100 `moment.locale(...)` que aún quedan
 * en scope de módulo; si no, vuelven a competir por el global.
 */
export const APP_MOMENT_LOCALE = "es-mx";

moment.locale(APP_MOMENT_LOCALE);

if (MazPicker && MazPicker.props && MazPicker.props.locale) {
  MazPicker.props.locale.default = APP_MOMENT_LOCALE;
} else if (process.env.NODE_ENV !== "production") {
  // Falla ruidosa si una actualización de maz-ui cambia la forma del componente.
  console.error(
    "[momentLocale] No se pudo fijar MazPicker.props.locale.default. " +
      'Revisar maz-ui: agregar locale="es-mx" en cada <MazPicker> con calendario.'
  );
}

if (process.env.NODE_ENV !== "production") {
  window.moment = moment; // ayuda de depuración desde la consola del navegador
}

export default moment;
